import { ContentState, EditorState, convertToRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import { useState, useImperativeHandle, forwardRef, useEffect } from 'react';
import { Editor } from "react-draft-wysiwyg";

import { Box } from '@mui/material';
import { colors } from 'constants/theme';

import 'draft-js/dist/Draft.css';
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";

/** STYLE */
import { styled } from '@mui/material/styles';

const CustomEditor = styled(Box)(({ theme }) => ({
  '.custom-editor-wrapper': {
    border: `1px solid ${colors.black12}`,
    '.custom-editor-toolbar': {
      borderWidth: '0 0 1px 0',
      borderColor: colors.black12,
      '> div': {
        marginBottom: 0,
        '&.rdw-fontfamily-wrapper': {
          display: 'none'
        },
        '&.rdw-fontsize-wrapper': {
          '.rdw-dropdown-selectedtext': {
            minWidth: 40
          }
        },
        '.rdw-dropdown-carettoopen': {
          top: '50%',
          transform: 'translateY(-50%)'
        },
        '.rdw-dropdown-optionwrapper': {
          borderColor: colors.black12,
          boxShadow: 'none',
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': {
            width: 3
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: colors.disabledGray,
            borderRadius: 100
          }
        },
        '> div': {
          marginBottom: 6
        }
      },
      '.rdw-option-wrapper, .rdw-dropdown-wrapper': {
        borderColor: colors.black12,
        '&:hover': {
          boxShadow: 'none',
          backgroundColor: colors.extraLightGray,
        },
        '&.rdw-option-active': {
          boxShadow: 'none',
          borderColor: colors.lightGray,
          backgroundColor: colors.lightGray,
        },
        '.rdw-dropdown-selectedtext': {
          color: colors.textPrimary,
          textDecoration: 'none'
        }
      }
    },
    '.custom-editor-text': {
      minHeight: 800,
      padding: '0 16px'
    }
  }
}))

type InputTextEditorProps = {
  value?: string
  setValue?: (val: string) => void
  disabled?: boolean
  addToolbar?: any
}

export type InputTextEditorRef = {
  undo: () => void
  redo: () => void
}

const InputTextEditor = forwardRef<InputTextEditorRef, InputTextEditorProps>((props, ref) => {

  const prepareDraft = (value: string) => {
    const draft = htmlToDraft(value);
    const contentState = ContentState.createFromBlockArray(draft.contentBlocks);
    const editorStateDraft = EditorState.createWithContent(contentState);

    return editorStateDraft;
  };

  const [editorState, setEditorState] = useState(props.value ? prepareDraft(props.value) : EditorState.createEmpty())

  // อัปเดต editorState เมื่อ props.value เปลี่ยน (เช่น เมื่อ clear หรือ load note)
  // แต่ต้องเช็คว่า value ที่แปลงเป็น HTML แล้วไม่เท่ากับ current content เพื่อป้องกัน infinite loop
  useEffect(() => {
    if (props.value !== undefined) {
      const currentHtml = draftToHtml(convertToRaw(editorState.getCurrentContent()));
      // เช็คว่า value เปลี่ยนจริงๆ หรือไม่ (ไม่ใช่จากการที่เรา setValue)
      if (currentHtml !== props.value) {
        const newEditorState = props.value ? prepareDraft(props.value) : EditorState.createEmpty();
        setEditorState(newEditorState);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.value]);

  const onEditorStateChange = (editorStateValue: EditorState) => {
    const forFormik = draftToHtml(convertToRaw(editorStateValue.getCurrentContent()));
    props.setValue && props.setValue(forFormik);
    setEditorState(editorStateValue);
  }

  useImperativeHandle(ref, () => ({
    undo: () => {
      const undoState = EditorState.undo(editorState);
      if (undoState !== editorState) {
        onEditorStateChange(undoState);
      }
    },
    redo: () => {
      const redoState = EditorState.redo(editorState);
      if (redoState !== editorState) {
        onEditorStateChange(redoState);
      }
    }
  }));

  return (
    <CustomEditor>
      <Editor
        editorState={editorState}
        wrapperClassName="custom-editor-wrapper"
        toolbarClassName="custom-editor-toolbar custom-scroll"
        editorClassName="custom-editor-text"
        onEditorStateChange={onEditorStateChange}
        toolbarHidden={props.disabled}
        readOnly={props.disabled}
        editorStyle={{
          backgroundColor: props.disabled ? "#F0F0F0" : "white",
        }}
      toolbarCustomButtons={props.addToolbar ? [props.addToolbar] : []}
      toolbar={{
        options: ['inline', 'blockType', 'fontSize', 'list', 'textAlign', 'link', 'emoji', 'image']
      }}
      />
    </CustomEditor>
  )
})

InputTextEditor.displayName = 'InputTextEditor';

export default InputTextEditor;
